import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { CheckCircle2, Download, MousePointerClick, Radio, XCircle } from 'lucide-react';
import { useMemo, useState } from 'react';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { EmptyState } from '@/components/shared/empty-state';
import { Spinner } from '@/components/shared/loading';
import { SectionCard } from '@/components/shared/section-card';
import { StatCard } from '@/components/shared/stat-card';
import { ToneBadge } from '@/components/shared/tone-badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { api } from '@/lib/api';
import { fmtNum } from '@/lib/format';
import { useAppCtx } from './app-layout';

const RANGES = { '1h': 3_600_000, '24h': 86_400_000, '7d': 7 * 86_400_000, '30d': 30 * 86_400_000 } as const;
const SERIES = [
  ['accepted', 'var(--chart-1)'],
  ['failed', 'var(--chart-2)'],
  ['clicked', 'var(--chart-3)'],
] as const;

export function Analytics() {
  const { app } = useAppCtx();
  const [range, setRange] = useState<keyof typeof RANGES>('24h');
  const window = useMemo(() => {
    const to = new Date();
    return { from: new Date(to.getTime() - RANGES[range]).toISOString(), to: to.toISOString() };
  }, [range]);
  const granularity = range === '1h' || range === '24h' ? 'minute' : 'hour';
  const query = { ...window };

  const summary = useQuery({ queryKey: ['summary', app.id, window], queryFn: () => api<{ accepted: number; failed: number; clicked: number; received: null }>(`/admin/v1/apps/${app.id}/analytics/summary`, { query }), placeholderData: keepPreviousData });
  const ts = useQuery({ queryKey: ['ts', app.id, window, granularity], queryFn: () => api<{ data: Array<{ bucket: string; event_type: string; n: number }> }>(`/admin/v1/apps/${app.id}/analytics/timeseries`, { query: { ...query, granularity } }), placeholderData: keepPreviousData });
  const errors = useQuery({ queryKey: ['errors', app.id, window], queryFn: () => api<{ data: Array<{ error_code: string; platform: string | null; n: number }> }>(`/admin/v1/apps/${app.id}/analytics/errors`, { query }), placeholderData: keepPreviousData });

  // Fetched through the API client: a plain link would not carry the in-memory access token.
  const exportCsv = async () => {
    const csv = await api<string>(`/admin/v1/apps/${app.id}/analytics/export.csv`, { query: { ...query, granularity } });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `analytics-${app.slug}-${range}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const chart = useMemo(() => {
    const byBucket = new Map<string, Record<string, number | string>>();
    for (const r of ts.data?.data ?? []) {
      const row = byBucket.get(r.bucket) ?? { bucket: r.bucket };
      row[r.event_type] = r.n;
      byBucket.set(r.bucket, row);
    }
    return [...byBucket.values()];
  }, [ts.data]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs value={range} onValueChange={(v) => setRange(v as keyof typeof RANGES)}>
          <TabsList>{Object.keys(RANGES).map((r) => <TabsTrigger key={r} value={r}>Last {r}</TabsTrigger>)}</TabsList>
        </Tabs>
        <Button variant="outline" onClick={() => void exportCsv()}><Download /> CSV</Button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="accepted" value={fmtNum(summary.data?.accepted)} icon={CheckCircle2} accent="bg-success/10 text-success" loading={summary.isLoading} />
        <StatCard label="failed" value={fmtNum(summary.data?.failed)} icon={XCircle} accent="bg-destructive/10 text-destructive" loading={summary.isLoading} />
        <StatCard label="clicked" value={fmtNum(summary.data?.clicked)} icon={MousePointerClick} accent="bg-primary/10 text-primary" loading={summary.isLoading} />
        <StatCard label="received" value={<ToneBadge>not available</ToneBadge>} icon={Radio} hint="Requires the mobile SDK (Track B)." />
      </div>
      <SectionCard className={ts.isPlaceholderData ? "opacity-60 transition-opacity" : "transition-opacity"} title="Over time" description="Counts update about once a minute.">
        {ts.isLoading ? <Spinner /> : chart.length === 0 ? <EmptyState title="No events in this range" hint="Counts update about once a minute." /> : (
          <div className="h-72">
            <ResponsiveContainer>
              <LineChart data={chart} margin={{ left: -16, right: 8 }}>
                <CartesianGrid stroke="var(--border)" vertical={false} />
                <XAxis dataKey="bucket" tickFormatter={(b: string) => new Date(b).toLocaleTimeString([], granularity === 'minute' ? { hour: '2-digit', minute: '2-digit' } : { month: 'short', day: 'numeric', hour: '2-digit' })} fontSize={11} stroke="var(--muted-foreground)" tickLine={false} axisLine={false} />
                <YAxis allowDecimals={false} fontSize={11} stroke="var(--muted-foreground)" tickLine={false} axisLine={false} />
                <Tooltip
                  labelFormatter={(b) => new Date(String(b)).toLocaleString()}
                  contentStyle={{ background: 'var(--popover)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', color: 'var(--popover-foreground)', fontSize: 12 }}
                />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                {SERIES.map(([k, color]) => <Line key={k} type="monotone" dataKey={k} stroke={color} strokeWidth={2} dot={false} connectNulls />)}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </SectionCard>
      <SectionCard title="Failures by reason" flush>
        {errors.data?.data.length === 0 ? <EmptyState icon={CheckCircle2} title="No failures" /> : (
          <Table>
            <TableHeader><TableRow><TableHead className="pl-5">Error code</TableHead><TableHead>Platform</TableHead><TableHead className="pr-5 text-right">Count</TableHead></TableRow></TableHeader>
            <TableBody>
              {errors.data?.data.map((e, i) => (
                <TableRow key={i}>
                  <TableCell className="pl-5 font-mono text-xs">{e.error_code ?? 'unknown'}</TableCell>
                  <TableCell>{e.platform ?? '—'}</TableCell>
                  <TableCell className="pr-5 text-right tabular-nums">{fmtNum(e.n)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </SectionCard>
    </div>
  );
}
